/*
 * Created on Thu Oct 12 2023 10:25:15 AM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import { CheckCircle, Eye, EyeOff, LogIn } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  getResetPasswordConfirmation,
  resetPassword,
  confirmUser,
  updatePassword
} from "../../api/action/user";
import StyledTooltip from '../../components/StyledTooltip.jsx';


const ResetPassword = (props) => {
  const { confirmUser: confirmUserAction, updatePassword: updatePasswordAction } = props;
  const [flag, setFlag] = useState(false);
  const [error, setError] = useState("");
  const [, setStatus] = useState(null);
  const [istrigered, setIsTrigered] = useState(false);

  const [, setShowComponent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [, setToken] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRules, setPasswordRules] = useState();

  // Renamed from 'user' to 'userData' to avoid conflict
  const [userData, setUserData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleClickShowPassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
    setShowConfirmPassword((prevShowConfirmPassword) => !prevShowConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const [password, setPassword] = useState(''); // Store the password separately
  const [confirmPassword, setConfirmPassword] = useState(''); // Store confirm password separately

  useEffect(() => {
    const checkPasswordValidity = (pwd) => { // Helper function
      if (!pwd) return false;
      const rules = [
        { text: "At least 8 characters", valid: pwd.length >= 8 },
        { text: "Spaces not allowed", valid: !/\s/.test(pwd) },
        { text: "1 uppercase letter (A-Z)", valid: /[A-Z]/.test(pwd) },
        { text: "1 lowercase letter (a-z)", valid: /[a-z]/.test(pwd) },
        { text: "1 number (0-9)", valid: /[0-9]/.test(pwd) },
        { text: "1 special character (!@#$)", valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(pwd) },
      ];
      return rules.every(rule => rule.valid);
    };

    setIsPasswordValid(checkPasswordValidity(password)); // Check validity and update state

    const formattedRules = (checkPasswordValidity(password)) ? "" : `𝗣𝗮𝘀𝘀𝘄𝗼𝗿𝗱 𝗺𝘂𝘀𝘁 𝗰𝗼𝗻𝘁𝗮𝗶𝗻:\n${[
      { text: "At least 8 characters", valid: password.length >= 8 },
      { text: "Spaces not allowed", valid: !/\s/.test(password) },
      { text: "1 uppercase letter (A-Z)", valid: /[A-Z]/.test(password) },
      { text: "1 lowercase letter (a-z)", valid: /[a-z]/.test(password) },
      { text: "1 number (0-9)", valid: /[0-9]/.test(password) },
      { text: "1 special character (!@#$)", valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password) },
    ].map((rule) => `${rule.valid ? " " : "x"} ${rule.text}`).join("\n")
      }`;

    setPasswordRules(formattedRules);

  }, [password]); // Only depend on password

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "newPassword") {
      setPassword(value); // Update password state
      setUserData(prev => ({ ...prev, newPassword: value })); //Update userData state
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
      setUserData(prev => ({ ...prev, confirmPassword: value })); //Update userData state
    } else {
      setUserData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    // Validate password and confirm password
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!isPasswordValid) {
      setError("Please ensure your password meets all the criteria.");
      return;
    }

    // Prepare updated user object (with new password)
    const updatedUser = {
      ...userData, // existing userData data (email, etc.)
      password: password, // new password to update
      modifiedBy: userData.firstName + " " + userData.lastName, // optional, modify if needed
    };

    // Call the updatePassword action
    updatePasswordAction(updatedUser) // Pass the updated user object here
      .then((res) => {
        if (res.message === "Password updated") {
          setError("User password has been updated successfully.");
          setFlag(true);

          // Optionally clear user-related data from localStorage
          localStorage.removeItem("reqUser");
          localStorage.removeItem("reqToken");
        }
        setSuccessMessage(true);
      })
      .catch((err) => {
        setFlag(false);
        setError(err?.message || err || "Failed to update password");
      });
  };


  const handleConfirm = useCallback((token) => {
    setIsTrigered(true);

    confirmUserAction(token)
      .then((response) => response.json())
      .then((data) => {
        setStatus(data.status);

        // If backend sends an error message (expired / invalid token)
        if (data.message) {
          setError(data.message);
          return;
        }

        // For ALL users → allow password setup (reset flow)
        setFlag(true);
        setShowComponent(true);  // show SetPassword component

        // Store required details
        localStorage.setItem("reqToken", token);
        localStorage.setItem("reqUser", data.email);

        const reqUser = localStorage.getItem("reqUser");

        setUserData((prevUser) => ({
          ...prevUser,
          email: reqUser,
          ...data,
        }));
      })
      .catch((error) => {
        setError(error.message || "An error occurred.");
      });
  }, [confirmUserAction]);



  useEffect(() => {
    // if (cond) {
    //   navigate("/products");
    //   return;
    // }

    const queryParams = new URLSearchParams(window.location.search);
    const term = queryParams.get("token");

    if (term) {
      setToken(term);
      if (!istrigered) {
        handleConfirm(term);
      }
    }
  }, [handleConfirm, istrigered]);

  return (
    <main className="flex min-h-screen w-full items-center justify-center px-4 py-10">
      <section className="w-full max-w-[500px] text-center">
        {flag ? (
          successMessage ? (
            <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-lg">
              <div className="mb-4 flex items-center justify-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-500" />
                <p className="text-lg font-semibold text-slate-900">
                  Account activated successfully!
                </p>
              </div>
              <p className="mb-5 font-semibold text-slate-700">
                Click the button below to go to the Sign-In page.
              </p>
              <a
                href="/sign-in"
                className="inline-flex items-center justify-center gap-2 text-blue-600 transition hover:text-blue-700 hover:underline"
              >
                <LogIn className="h-4 w-4" />
                <span>Sign in</span>
              </a>
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl border border-slate-100 bg-white p-8 shadow-lg">
              <h1 className="mb-6 text-lg font-semibold text-slate-900">
                Please set a new password to continue.
              </h1>
              <form onSubmit={handleSubmit} className="w-full">
                <div className="mx-auto flex w-full max-w-[300px] flex-col gap-4">
                  <label className="block text-left">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
                    <input
                      id="instructor-email-reddittextfield"
                      name="email"
                      value={userData.email}
                      disabled
                      type="email"
                      required
                      className="w-full rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-700 outline-none focus:border-blue-500"
                    />
                  </label>

                  <StyledTooltip className="w-full" title={passwordRules} placement="left" arrow>
                    <label className="relative block w-full text-left">
                      <span className="mb-1 block text-sm font-medium text-slate-700">Password</span>
                      <input
                        name="newPassword"
                        value={userData.newPassword}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        className="w-full rounded-md border border-slate-300 bg-slate-100 px-3 py-2 pr-10 text-slate-700 outline-none focus:border-blue-500"
                      />
                      <button
                        type="button"
                        aria-label="Toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        className="absolute right-2 top-[30px] rounded-md p-1 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </label>
                  </StyledTooltip>

                  <label className="relative block text-left">
                    <span className="mb-1 block text-sm font-medium text-slate-700">Confirm Password</span>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={userData.confirmPassword}
                      required
                      name="confirmPassword"
                      onChange={handleChange}
                      className={`w-full rounded-md border bg-slate-100 px-3 py-2 pr-10 text-slate-700 outline-none focus:border-blue-500 ${
                        isSubmitted && userData.confirmPassword !== userData.newPassword
                          ? "border-red-500"
                          : "border-slate-300"
                      }`}
                    />
                    <button
                      type="button"
                      aria-label="Toggle confirm password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      className="absolute right-2 top-[30px] rounded-md p-1 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </label>

                  {isSubmitted && userData.confirmPassword !== userData.newPassword && (
                    <p className="text-sm text-red-500">Passwords do not match</p>
                  )}

                  <button
                    id="instructor-submit-reset-password-button"
                    className={`mt-2 w-full rounded-md py-2 font-bold text-white transition ${
                      isPasswordValid
                        ? "bg-slate-900 hover:bg-slate-800"
                        : "cursor-not-allowed bg-slate-400"
                    }`}
                    type="submit"
                    disabled={!isPasswordValid}
                  >
                    Save Password
                  </button>
                </div>
              </form>
            </div>
          )
        ) : (
          error && <p className="mb-5 text-center text-red-600">{error}</p>
        )}
      </section>
    </main>
  );
};

function mapStateToProps(state) {
  // Safely access nested properties with fallbacks
  const isAuthenticated = state?.auth?.data?.isAuthenticated ?? false;
  const user = state?.auth?.data?.user ?? null;

  return {
    isAuthenticated: isAuthenticated,
    user: user
  };
}

export default connect(() => mapStateToProps, {
  resetPassword,
  getResetPasswordConfirmation,
  updatePassword,
  confirmUser
  // setRettedAt,
})(ResetPassword);
